import clsx from 'clsx';

import { SelectOptionValue, SelectOption } from '@stern-ui/ui';

import { FieldErrors, useFieldContext } from '../';
import { SelectV2, Label } from '../../Elements';

type SelectFieldProps = {
	label: string;
	labelVisible?: boolean;
	options: SelectOption[] | [];
	isMulti?: boolean;
	required?: boolean;
	isCompact?: boolean;
	showSelectAll?: boolean;
	isDisabled?: boolean;
	customLabel?: (data: SelectOption) => React.ReactElement;
	placeholder?: string;
	containerClass?: string;
};

export const SelectField = ({
	label,
	labelVisible = true,
	options,
	isMulti,
	required,
	isCompact,
	showSelectAll,
	isDisabled,
	customLabel,
	placeholder,
	containerClass,
}: SelectFieldProps) => {
	const field = useFieldContext<string | string[] | SelectOptionValue[]>();

	const checkValue = (option: SelectOption, val: unknown) => {
		// check if the option matches the value considering it could be an object or just a plain value
		if (typeof val === 'object' && val !== null) {
			return Object.values(val).includes((option.value as unknown as SelectOptionValue).id);
		}
		return val === option.value;
	};

	// Map the value from form state to the corresponding option object
	const getSelectedOption = () => {
		if (isMulti && Array.isArray(field.state.value)) {
			//return options.filter((opt) => field.state.value.includes(opt.value as string));
			return options.filter(
				(option) =>
					field.state.value?.length >= 1 &&
					(field.state.value as SelectOptionValue[])?.some((elm: SelectOptionValue) =>
						checkValue(option, elm)
					)
			);
		}
		return (
			options.find((opt) => field.state.value && checkValue(opt, field.state.value)) || null
		);
	};

	// Pass only the value to TanStack Forms
	const handleOnChange = (selected: SelectOption | SelectOption[]) => {
		if (isMulti) {
			const values = Array.isArray(selected) ? selected.map((opt) => opt.value) : [];
			field.handleChange(values as string[]);
		} else {
			field.handleChange(((selected as SelectOption)?.value ?? '') as string);
		}
	};

	// hide the error messages until the field has been touched
	const hideErrors = !field.state.meta.isTouched;

	return (
		<div className={clsx('flex flex-col', containerClass)}>
			<Label
				htmlFor={field.name}
				label={label}
				required={required}
				className={clsx('mb-1', labelVisible ? '' : 'sr-only')}
				disabled={isDisabled}
			/>
			<SelectV2
				name={field.name}
				options={options}
				handleChange={handleOnChange}
				value={getSelectedOption()} // Pass option object(s) for label display
				isMulti={isMulti}
				isCompact={isCompact}
				placeholder={placeholder || 'Select'}
				showSelectAll={showSelectAll}
				isDisabled={isDisabled}
				customLabel={customLabel}
			/>
			<FieldErrors meta={field.state.meta} name={field.name} hideErrors={hideErrors} />
		</div>
	);
};
